import * as React from 'react';
import { PageTemplate } from 'components/PageTemplate';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ErrorMessage } from 'components/ErrorMessage';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';

export interface StandardListPageProps {
  /** Page title */
  title: string;
  /** Optional header control (e.g., Add button) */
  headerControl?: React.ReactNode;
  /** Loading state */
  loading: boolean;
  /** Error object (null/undefined when no error) */
  error?: { message: string } | null;
  /** Error title to show */
  errorTitle?: string;
  /** Message when data is empty */
  noDataMessage?: string;
  /** Whether data is empty (separate from loading/error) */
  isEmpty?: boolean;
  /** data-testid for the page wrapper */
  'data-testid'?: string;
  /** Loading indicator testid */
  loadingTestId?: string;
  /** Error testid */
  errorTestId?: string;
  /** No data testid */
  noDataTestId?: string;
  /** Children: the actual table content, only rendered when not loading/error/empty */
  children: React.ReactNode;
}

export const StandardListPage: React.FC<StandardListPageProps> = (props) => {
  const {
    title,
    headerControl,
    loading,
    error,
    errorTitle,
    noDataMessage,
    isEmpty,
    children,
    ...testIds
  } = props;

  let content: React.ReactNode;

  if (loading) {
    content = <LoadingIndicator data-testid={testIds.loadingTestId} />;
  } else if (error) {
    content = (
      <ErrorMessage
        title={errorTitle || 'Error'}
        message={error.message}
        variant="block"
        data-testid={testIds.errorTestId}
      />
    );
  } else if (isEmpty) {
    content = (
      <Typography data-testid={testIds.noDataTestId}>{noDataMessage || 'No data'}</Typography>
    );
  } else {
    content = children;
  }

  return (
    <PageTemplate title={title} headerControl={headerControl} data-testid={testIds['data-testid']}>
      {content}
    </PageTemplate>
  );
};
